/**
 * Silently upgrades the user to the latest frontend assets when a new backend
 * version is detected (via VERSION_MISMATCH_EVENT, fired by the axios
 * interceptor). Renders nothing — there is no banner.
 *
 * We deploy multiple times a day, so a persistent "please refresh" banner was
 * nagging. Instead we refresh at the least disruptive moment, never mid-task:
 *  1. On the next client-side navigation — the page the user lands on is clean.
 *  2. When the tab becomes hidden (user switched away / minimized) — reloads in
 *     the background so a screen with live/polling data is fresh on return.
 *  3. After the user has been idle on the current page for IDLE_REFRESH_MS —
 *     catches a polling screen the user walked away from without navigating.
 *
 * (2) and (3) are skipped while any form has unsaved changes (tracked via the
 * unsaved-changes registry) to avoid a surprise "Leave site?" prompt or data
 * loss. (1) is always safe because the destination page has no in-flight edits.
 *
 * None of them fire until a NEW BUNDLE IS ACTUALLY PUBLISHED — see
 * `isNewBundlePublished` below for why the backend's version alone is not
 * enough to go on.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { VERSION_MISMATCH_EVENT } from '../api/axiosInstance';
import { isCapacitor } from '../api/config';
import { fetchWithDeadline } from '../api/fetchDeadline';
import { hasUnsavedChanges } from '../utils/unsavedChangesRegistry';

/** Idle time (no user interaction) before we silently refresh in place. */
export const IDLE_REFRESH_MS = 2 * 60 * 1000;

/** How long to wait before re-asking whether the new bundle has landed. */
export const BUNDLE_PUBLISH_POLL_MS = 60 * 1000;

/** Stop asking after this long — a mismatch we are not going to resolve. */
export const BUNDLE_PUBLISH_POLL_TIMEOUT_MS = 30 * 60 * 1000;

const INTERACTION_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
] as const;

/**
 * The module entry a document boots from, e.g. "/assets/index-B3tBRw3X.js".
 *
 * Vite content-hashes it, so it changes on every build — which makes it the
 * cheapest available answer to "is the index.html being served now the same
 * document this page was loaded from?". Compared as the raw attribute, because
 * both sides are written by the same Vite build and a URL round-trip would only
 * add ways to disagree.
 */
function entrySrc(doc: Document): string | null {
  const scripts = doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]');
  for (const script of scripts) {
    const src = script.getAttribute('src');
    // Vite's dev server injects `/@vite/client` as the FIRST module script, and
    // its path never changes across rebuilds — so taking it as the entry would
    // make every probe answer "same bundle" forever. A built index.html carries
    // exactly one module script (the preceding OAuth trampoline has neither
    // `type` nor `src`), so skipping Vite's own `/@` namespace is the whole
    // difference between the two environments.
    if (src && !src.startsWith('/@')) return src;
  }
  return null;
}

/**
 * Has a new frontend bundle actually been published?
 *
 * The pipeline deploys the backend FIRST and the frontend up to ~12 minutes
 * later (`deploy-frontend` needs `deploy-backend`, and its own S3 asset sync
 * runs for minutes — measured at 12m02s on 2026-09-02). For that entire window
 * every open session sees a changed backend version while index.html, and so
 * every chunk the running page can reach, is still the OLD build. Reloading
 * then cannot resolve anything: it re-serves the same bundle, and fired on a
 * navigation it aborts the route chunk that navigation was importing — which is
 * how a user ends up on the app error boundary instead of the page they asked
 * for (WI ad05f876).
 *
 * So "the backend changed" is not treated as "there is an update to take".
 *
 * An INDETERMINATE probe answers `false` — "not yet", keep polling — rather than
 * waving the reload through. A non-OK response, a network error and a response
 * with no entry tag in it are all most likely DURING a deploy, which is exactly
 * the window this guard exists for, so failing open there would reproduce the
 * original bug for precisely the population it protects. Because the caller
 * retries, a transient answer of `false` costs nothing but one more poll.
 *
 * The one case that answers `true` is a running document with no entry tag of
 * its own: no amount of polling can resolve that, so it degrades to the
 * pre-existing behaviour instead of disabling the refresh forever.
 */
async function isNewBundlePublished(): Promise<boolean> {
  const current = entrySrc(document);
  if (!current) return true;
  try {
    // Not `axiosInstance`: this is the static shell, not an API call, so it
    // has no business in the auth/refresh interceptors — which is exactly the
    // bypass `fetchWithDeadline` exists for.
    //
    // `cache: 'no-store'` covers the BROWSER cache only. The edge is covered by
    // the deploy itself, which uploads index.html with
    // `no-cache, no-store, must-revalidate` (deploy.yml, "Deploy to S3") —
    // verified against production, which serves that header and reports
    // `x-cache: Miss from cloudfront`.
    const response = await fetchWithDeadline('/index.html', { cache: 'no-store' });
    if (!response.ok) return false;
    const published = entrySrc(
      new DOMParser().parseFromString(await response.text(), 'text/html')
    );
    if (published === null) return false;
    return published !== current;
  } catch {
    return false;
  }
}

async function clearCachesAndReload() {
  // Clear all caches and unregister service workers so the browser fetches the
  // new assets rather than serving stale ones.
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
  }

  window.location.reload();
}

export function VersionChecker() {
  const location = useLocation();
  const currentPath = useRef(location.pathname);
  const updatePending = useRef(false);
  // A mismatch has been seen but the new bundle has not landed yet. Triggers 2
  // and 3 are one-shot, so they consult this to keep looking rather than firing
  // once, seeing nothing to do, and never re-arming.
  const awaitingBundle = useRef(false);
  const pathAtDetection = useRef<string | null>(null);
  const reloaded = useRef(false);

  // Track the latest route path so the (stable) detection handler can record
  // where the user was when the update was detected.
  useEffect(() => {
    currentPath.current = location.pathname;
  }, [location.pathname]);

  const safeReload = useCallback((requireClean: boolean) => {
    if (reloaded.current) return;
    // On Capacitor the app runs from bundled local assets, so reloading cannot
    // fetch a new build — updates ship via the OTA flow (apply-on-next-launch).
    // A mid-session reload would just disrupt the user without updating anything.
    if (isCapacitor) return;
    if (requireClean && hasUnsavedChanges()) return;
    reloaded.current = true;
    void clearCachesAndReload();
  }, []);

  // Detect a pending update. Record the path we were on so we don't count the
  // navigation that may have triggered detection as "the next navigation" —
  // then wait for the new bundle to actually be published before arming any of
  // the three triggers below, polling because the frontend lands minutes after
  // the backend that raised the mismatch.
  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let deadline = 0;

    const poll = async () => {
      if (cancelled) return;
      const published = await isNewBundlePublished();
      if (cancelled) return;
      if (published) {
        awaitingBundle.current = false;
        updatePending.current = true;
        // The tab may have been hidden the whole time we were waiting. Trigger 2
        // already fired and found nothing to do, and a hidden tab produces no
        // further visibilitychange, so take it here instead.
        if (document.hidden) safeReload(true);
        return;
      }
      if (Date.now() >= deadline) {
        // Give up rather than poll forever. Nothing is armed, so an unresolvable
        // mismatch simply never triggers a refresh — the app keeps working on
        // the bundle it has.
        awaitingBundle.current = false;
        return;
      }
      pollTimer = setTimeout(() => {
        void poll();
      }, BUNDLE_PUBLISH_POLL_MS);
    };

    const handleVersionMismatch = () => {
      // On Capacitor every trigger below is a no-op (`safeReload` returns early
      // — the app runs from bundled assets and updates ship via OTA), so the
      // whole poll would be minutes of fetch + parse for nothing.
      if (isCapacitor) return;
      pathAtDetection.current = currentPath.current;
      awaitingBundle.current = true;
      deadline = Date.now() + BUNDLE_PUBLISH_POLL_TIMEOUT_MS;
      void poll();
    };

    window.addEventListener(VERSION_MISMATCH_EVENT, handleVersionMismatch);
    return () => {
      cancelled = true;
      awaitingBundle.current = false;
      if (pollTimer) clearTimeout(pollTimer);
      window.removeEventListener(VERSION_MISMATCH_EVENT, handleVersionMismatch);
    };
  }, [safeReload]);

  // Trigger 1: refresh on the next client-side navigation.
  useEffect(() => {
    if (
      updatePending.current &&
      pathAtDetection.current !== null &&
      location.pathname !== pathAtDetection.current
    ) {
      safeReload(false);
    }
  }, [location.pathname, safeReload]);

  // Triggers 2 & 3: tab hidden, or foreground idle.
  useEffect(() => {
    let idleTimer: ReturnType<typeof setTimeout> | undefined;

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (updatePending.current) {
          safeReload(true);
        } else if (awaitingBundle.current) {
          // The bundle had not landed when this fired. Only an interaction
          // re-arms this timer, and a genuinely idle tab produces none — so
          // without re-arming here, the one tab that most wants a silent
          // background refresh is the one that never gets one.
          resetIdleTimer();
        }
      }, IDLE_REFRESH_MS);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        if (updatePending.current) safeReload(true);
      } else {
        resetIdleTimer();
      }
    };

    INTERACTION_EVENTS.forEach((evt) =>
      window.addEventListener(evt, resetIdleTimer, { passive: true })
    );
    document.addEventListener('visibilitychange', handleVisibility);
    resetIdleTimer();

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, resetIdleTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [safeReload]);

  return null;
}
